import { useEffect, useState, useCallback, useRef, memo } from "react";
import Modal from "@mui/material/Modal";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import { toast } from "react-toastify";
import { APIError } from "../../../core/utils";
import styled from "styled-components";

const MAX_REASONS = 3;

const AcceptRequest = ({
  actionloader,
  _actionloader,
  getReportedAds,
  adId,
}) => {
  const [open, setOpen] = useState(false);
  const [isLoading, _isLoading] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [query, setQuery] = useState("");
  const debounceTimeout = useRef(null);

  const handleOpen = () => {
    _isLoading(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedReasons([]);
    setQuery("");
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
  };

  const fetchReasons = useCallback(() => {
    const url = `${APP_URLS.LIST_SUGGESTION_TEXT}?filter[name]=${query}&filter[type]=4&limited_data=true`;
    API.get(url)
      .then((res) => {
        const { success, message, data } = res.data;
        if (success) {
          setReasons(data.items);
        } else {
          toast.error(message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch((error) => {
        const errorMsg = APIError(error);
        toast.error(errorMsg, { position: toast.POSITION.TOP_RIGHT });
      })
      .finally(() => {
        _isLoading(false);
      });
  }, [query]);

  useEffect(() => {
    if (!open) return;
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      fetchReasons();
    }, 500);

    return () => clearTimeout(debounceTimeout.current);
  }, [query, fetchReasons, open]);

  const addComment = (reason) => {
    if (selectedReasons.some((r) => r.id === reason.id)) {
      toast.error("Reason has already been added", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (selectedReasons.length >= MAX_REASONS) {
      toast.error("Maximum reasons already added", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setSelectedReasons((prev) => [...prev, reason]);
  };

  const removeComment = (indexToRemove) => {
    setSelectedReasons((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleAccept = () => {
    if (!selectedReasons || selectedReasons.length === 0) {
      toast.error("Add some reasons for accepting the request");
      return; // stop execution if no reasons
    }

    const params = {};
    selectedReasons.forEach((reason, index) => {
      params[`delete_reason_ids[${index}]`] = reason.id;
    });

    if (actionloader) return;
    _actionloader(true);

    API.post(
      `${APP_URLS.REPORTED_ADS}/${adId}/status`,
      { status: 1 },
      { params }
    )
      .then((res) => {
        const resp = res.data;
        if (resp.success === true) {
          toast.success(`Reported ad request rejected successfully`, {
            position: toast.POSITION.TOP_RIGHT,
          });
          getReportedAds();
        } else {
          toast.error(resp.message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(function (error) {
        let error_message = APIError(error);
        toast.error(error_message, {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => {
        _actionloader(false);
      });
  };

  return (
    <>
      <button className="accept" onClick={handleOpen} disabled={actionloader}>
        Accept
      </button>

      <Modal open={open} onClose={handleClose}>
        <Wrapper>
          <h5 className="modalTitle">Choose Reasons :</h5>
          <div className="modalBody">
            <input
              type="text"
              className="form-control"
              placeholder="Search Reason..."
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="cardContent">
              {reasons.length > 0
                ? reasons.map((reason) => (
                    <span
                      key={`option-${reason.id}`}
                      className="comments"
                      onClick={() => addComment(reason)}
                    >
                      {reason.name}
                    </span>
                  ))
                : !isLoading && (
                    <span className="disabledcomment">No Reasons Found</span>
                  )}
            </div>
            <div className="cardFooter">
              {selectedReasons.length > 0 ? (
                selectedReasons.map((reason, index) => (
                  <div
                    key={`selected-${reason.id}`}
                    className="d-flex align-items-center ps-3 py-1"
                  >
                    <span className="pe-2">{reason.name}</span>
                    <span
                      className="removeComment"
                      onClick={() => removeComment(index)}
                    >
                      x
                    </span>
                  </div>
                ))
              ) : (
                <div className="d-flex align-items-center ps-3 py-1">
                  <span className="pe-2 suggestion">
                    Please add some comments...
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="actionButtons">
            <button className="cancelButton" onClick={handleClose}>
              Cancel
            </button>
            <button className="acceptButton" onClick={handleAccept}>
              Accept
            </button>
          </div>
        </Wrapper>
      </Modal>
    </>
  );
};

export default memo(AcceptRequest);

export const Wrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  max-width: 500px;
  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  outline: 0;
  border-radius: 0.3rem;
  .modalTitle {
    line-height: 1.5;
    margin-bottom: 0;
    border-bottom: 1px solid #dee2e6;
    padding: 1rem;
  }
  .modalBody {
    padding: 32px 48px;
  }
  .cardContent {
    display: flex;
    flex-direction: column;
    max-height: 200px;
    overflow-y: auto;
    margin: 20px 0;
  }
  .comments {
    color: rgb(93, 120, 255);
    border: 1px solid rgb(93, 120, 255);
    border-radius: 5px;
    padding: 0px 4px;
    margin-bottom: 5px;
    word-break: break-word;
    width: fit-content;
    cursor: pointer;
  }
  .cardFooter {
    border: 1px solid rgba(0, 0, 0, 0.125);
    max-height: 85px;
    min-height: 84px;
    overflow-y: scroll;
  }
  .removeComment {
    height: 13px;
    width: 13px;
    border: 1px solid red;
    border-radius: 50%;
    text-align: center;
    line-height: 9px;
    color: red;
    font-size: 12px;
    cursor: pointer;
  }
  .actionButtons {
    display: flex;
    flex-direction: row;
    gap: 20px;
    justify-content: flex-end;
    padding: 1rem;
    button {
      align-self: center;
      border: none;
      height: 35px;
      border-radius: 5px;
      padding: 0px 20px;
      color: white;
      font-weight: bold;
    }
    .cancelButton {
      background-color: #607afc;
    }
    .acceptButton {
      background-color: #ff0018;
    }
  }
  .disabledcomment {
    opacity: 0.5;
    border-radius: 5px;
    padding: 0px 4px;
    margin-bottom: 5px;
    word-break: break-word;
    width: fit-content;
    font-size: 14px;
    color: red !important;
    border: 1px solid red !important;
    cursor: not-allowed !important;
  }
`;
